import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { ClientsApi } from 'entities/Clients/api/clients'
import { type RowClient } from 'entities/Clients/model/RowClient'
import { type IResponseHubSpotTeams } from 'entities/Clients/api/types'
import { ManageTeamsModal } from 'pages/clients/ui/actions/manageTeams/ui/ManageTeamsModal'
import { TeamsSelectFieldStore } from 'pages/clients/ui/shared/TeamsSelectField/store/teamsSelectFieldStore'

export const manageTeams = (
  row: RowClient,
  onSuccess: () => void,
  teamsList: IResponseHubSpotTeams[]
) => {
  const modalId = nanoid()
  const store = new TeamsSelectFieldStore()
  store.setTeamsList(teamsList)
  store.setSelectedTeamIds(row.origin.groups?.map((group) => group.integration_team_id) ?? [])

  const onClose = () => {
    modalStore.closeModal(modalId)
  }

  const onManageTeams = async (teams: number[], successTitle: string) => {
    try {
      await ClientsApi.manageHubSpotTeams(row.id, teams)
      onSuccess()
      onClose()
      toastStore.add({
        type: 'success',
        title: successTitle,
      })
    } catch (error) {
      logger.error(error)
      toastStore.add({
        type: 'error',
        title: 'Something went wrong, try again.',
      })
    }
  }

  const openConfirmModal = ({
    type,
    title,
    desc,
    primaryText,
    onConfirm,
  }: {
    type: ModalTypeList
    title: string
    desc: string
    primaryText: string
    onConfirm: () => Promise<void>
  }) => {
    const confirmModalId = nanoid()
    modalStore.addModal({
      type,
      id: confirmModalId,
      title,
      desc,
      width: 280,
      primaryAction: {
        text: primaryText,
        onAction: async () => {
          await onConfirm()
          modalStore.closeModal(confirmModalId)
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => modalStore.closeModal(confirmModalId),
      },
    })
  }

  const handleSave = async (teamIds: number[]) => {
    if (!row.origin.groups?.length) {
      await onManageTeams(teamIds, 'HubSpot teams updated')
      return
    }
    openConfirmModal({
      type: ModalTypeList.ALERT,
      title: 'Change HubSpot teams?',
      desc: 'Changing HubSpot teams may affect synced contacts and conversations. Some data may no longer be available.',
      primaryText: 'Change teams',
      onConfirm: () => onManageTeams(teamIds, 'HubSpot teams updated'),
    })
  }

  const handleDisconnectTeams = () => {
    openConfirmModal({
      type: ModalTypeList.WARNING,
      title: 'Disconnect all teams?',
      desc: 'Disconnecting all teams may affect synced contacts and conversations. Some data may no longer be available.',
      primaryText: 'Disconnect all teams',
      onConfirm: () => onManageTeams([], 'All HubSpot teams disconnected'),
    })
  }

  modalStore.addModal({
    id: modalId,
    title: 'Manage HubSpot teams',
    width: 480,
    pureContent: true,
    ModalContent: ManageTeamsModal,
    ModalContentProps: { row, onClose, handleSave, handleDisconnectTeams, store },
  })
}
